import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSearch, FaUndo } from 'react-icons/fa';
import { Input } from '../../../components/forms/Input';
import { Select } from '../../../components/forms/Select';
import { POLICY_STATUS_ORDER, PRODUCTS, STATUS_META } from '../constants';
import { usePasStore } from '../pasStore';
import type { Policy } from '../types';
import { formatCurrency, formatDate } from '../utils';
import { Button } from '../components/Button';
import { DataTable, type Column } from '../components/DataTable';
import { Card, PageHeader } from '../components/primitives';
import { PolicyStatusBadge } from '../components/StatusBadges';

interface Criteria {
  keyword: string;
  status: string;
  brokerId: string;
  carrierId: string;
  product: string;
  minPremium: string;
  maxPremium: string;
  effectiveFrom: string;
  effectiveTo: string;
}

const EMPTY: Criteria = {
  keyword: '',
  status: '',
  brokerId: '',
  carrierId: '',
  product: '',
  minPremium: '',
  maxPremium: '',
  effectiveFrom: '',
  effectiveTo: '',
};

export const SearchPage: React.FC = () => {
  const navigate = useNavigate();
  const policies = usePasStore((s) => s.policies);
  const brokers = usePasStore((s) => s.brokers);
  const carriers = usePasStore((s) => s.carriers);
  const [criteria, setCriteria] = useState<Criteria>(EMPTY);
  const [applied, setApplied] = useState<Criteria>(EMPTY);
  const [hasSearched, setHasSearched] = useState(false);

  const set = (patch: Partial<Criteria>) => setCriteria((c) => ({ ...c, ...patch }));

  const results = useMemo(() => {
    const kw = applied.keyword.trim().toLowerCase();
    const min = applied.minPremium ? Number(applied.minPremium) : null;
    const max = applied.maxPremium ? Number(applied.maxPremium) : null;
    return policies.filter((p) => {
      if (kw && !`${p.policyNumber} ${p.insured.name} ${p.insured.contactName}`.toLowerCase().includes(kw)) return false;
      if (applied.status && p.status !== applied.status) return false;
      if (applied.brokerId && p.broker.id !== applied.brokerId) return false;
      if (applied.carrierId && p.carrier.id !== applied.carrierId) return false;
      if (applied.product && p.product !== applied.product) return false;
      if (min !== null && p.annualPremium < min) return false;
      if (max !== null && p.annualPremium > max) return false;
      if (applied.effectiveFrom && p.effectiveDate < applied.effectiveFrom) return false;
      if (applied.effectiveTo && p.effectiveDate > applied.effectiveTo) return false;
      return true;
    });
  }, [policies, applied]);

  const columns: Column<Policy>[] = [
    { key: 'policyNumber', header: 'Policy', primary: true, sortValue: (p) => p.policyNumber, accessor: (p) => <div><span>{p.policyNumber}</span><span className="block text-xs font-normal" style={{ color: 'var(--color-text-secondary)' }}>{p.insured.name}</span></div> },
    { key: 'status', header: 'Status', sortValue: (p) => p.status, accessor: (p) => <PolicyStatusBadge status={p.status} /> },
    { key: 'broker', header: 'Broker', sortValue: (p) => p.broker.agency, accessor: (p) => <span className="text-sm">{p.broker.agency}</span> },
    { key: 'product', header: 'Product', sortValue: (p) => p.product, accessor: (p) => <span className="text-sm">{p.product}</span> },
    { key: 'effective', header: 'Effective', sortValue: (p) => p.effectiveDate, accessor: (p) => <span className="text-sm">{formatDate(p.effectiveDate)}</span> },
    { key: 'premium', header: 'Premium', align: 'right', sortValue: (p) => p.annualPremium, accessor: (p) => <span className="text-sm font-semibold">{formatCurrency(p.annualPremium)}</span> },
  ];

  const runSearch = () => {
    setApplied(criteria);
    setHasSearched(true);
  };
  const reset = () => {
    setCriteria(EMPTY);
    setApplied(EMPTY);
    setHasSearched(false);
  };

  return (
    <div>
      <PageHeader title="Search" subtitle="Find policies across the entire book with detailed criteria." />

      <Card className="mb-6">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            runSearch();
          }}
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Input label="Keyword" placeholder="Policy #, insured, contact…" value={criteria.keyword} onChange={(e) => set({ keyword: e.target.value })} leftIcon={<FaSearch className="h-3.5 w-3.5" style={{ color: 'var(--color-text-secondary)' }} />} fullWidth />
            <Select label="Status" options={[{ value: '', label: 'Any status' }, ...POLICY_STATUS_ORDER.map((s) => ({ value: s, label: STATUS_META[s].label }))]} value={criteria.status} onChange={(e) => set({ status: e.target.value })} fullWidth />
            <Select label="Product" options={[{ value: '', label: 'Any product' }, ...PRODUCTS.map((p) => ({ value: p, label: p }))]} value={criteria.product} onChange={(e) => set({ product: e.target.value })} fullWidth />
            <Select label="Broker" options={[{ value: '', label: 'Any broker' }, ...brokers.map((b) => ({ value: b.id, label: b.agency }))]} value={criteria.brokerId} onChange={(e) => set({ brokerId: e.target.value })} fullWidth />
            <Select label="Carrier" options={[{ value: '', label: 'Any carrier' }, ...carriers.map((c) => ({ value: c.id, label: c.name }))]} value={criteria.carrierId} onChange={(e) => set({ carrierId: e.target.value })} fullWidth />
            <div className="grid grid-cols-2 gap-3">
              <Input label="Min premium" type="number" value={criteria.minPremium} onChange={(e) => set({ minPremium: e.target.value })} fullWidth />
              <Input label="Max premium" type="number" value={criteria.maxPremium} onChange={(e) => set({ maxPremium: e.target.value })} fullWidth />
            </div>
            <Input label="Effective from" type="date" value={criteria.effectiveFrom} onChange={(e) => set({ effectiveFrom: e.target.value })} fullWidth />
            <Input label="Effective to" type="date" value={criteria.effectiveTo} onChange={(e) => set({ effectiveTo: e.target.value })} fullWidth />
          </div>
          <div className="mt-5 flex items-center gap-2">
            <Button type="submit" leftIcon={<FaSearch className="h-3 w-3" />}>Search</Button>
            <Button type="button" variant="ghost" leftIcon={<FaUndo className="h-3 w-3" />} onClick={reset}>Reset</Button>
          </div>
        </form>
      </Card>

      {hasSearched && (
        <>
          <p className="mb-3 text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>
            {results.length} {results.length === 1 ? 'result' : 'results'}
          </p>
          <DataTable
            columns={columns}
            data={results}
            getRowId={(p) => p.id}
            onRowClick={(p) => navigate(`/policy-admin/policies/${p.id}`)}
            initialSort={{ key: 'premium', direction: 'desc' }}
            emptyIcon={<FaSearch />}
            emptyTitle="No policies match your criteria"
            emptyMessage="Try broadening your search."
          />
        </>
      )}
    </div>
  );
};
